import styled from "styled-components";
import { Users, Maximize, Wifi, Coffee } from "lucide-react";
import {
  Container,
  SectionHeader,
  Subtitle,
  Title,
  Description,
} from "./Section";

const RoomsSection = styled.section`
  padding: 120px 24px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 80px 24px;
  }
`;

const RoomsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RoomCard = styled.div`
  background: #ffffff;
  overflow: hidden;
  border: 1px solid #e5e1dc;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.12);
    border-color: transparent;
  }
`;

const RoomImage = styled.div`
  background: linear-gradient(135deg, #e5e1dc 0%, #d4cfc7 100%);
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &:after {
    content: "${(props) => props.$roomType}";
    font-size: 14px;
    color: #8b7355;
    font-weight: 500;
    letter-spacing: 1px;
  }
`;

const PriceTag = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  background: #1a1a1a;
  padding: 12px 20px;
  font-weight: 600;
  color: #c9a86c;
  font-size: 18px;
`;

const RoomContent = styled.div`
  padding: 32px;
`;

const RoomTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
  letter-spacing: 0.3px;
`;

const RoomDesc = styled.p`
  font-size: 14px;
  color: #8b7355;
  line-height: 1.8;
  margin-bottom: 24px;
  font-weight: 400;
`;

const RoomFeatures = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;

  svg {
    color: #c9a86c;
    width: 16px;
    height: 16px;
  }
`;

const BookButton = styled.button`
  width: 100%;
  background: transparent;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 14px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 2px;
  text-transform: uppercase;

  &:hover {
    background: #1a1a1a;
    color: #faf8f5;
  }
`;

const Rooms = () => {
  const rooms = [
    {
      type: "Standard Room",
      price: "$45",
      description:
        "Cozy room perfect for solo travelers or couples, featuring essential amenities.",
      maxGuests: 2,
      size: "20 m²",
      features: ["WiFi", "Breakfast"],
    },
    {
      type: "Deluxe Room",
      price: "$75",
      description:
        "Spacious room with modern design and stunning city views for a premium stay.",
      maxGuests: 3,
      size: "32 m²",
      features: ["WiFi", "Breakfast"],
    },
    {
      type: "Suite Room",
      price: "$120",
      description:
        "Luxurious suite with separate living area and premium amenities.",
      maxGuests: 4,
      size: "45 m²",
      features: ["WiFi", "Breakfast"],
    },
    {
      type: "Dormitory Bed",
      price: "$20",
      description:
        "Shared accommodation perfect for budget travelers and backpackers.",
      maxGuests: 1,
      size: "Shared",
      features: ["WiFi", "Locker"],
    },
    {
      type: "Family Room",
      price: "$95",
      description:
        "Spacious room ideal for families with children, featuring extra beds.",
      maxGuests: 5,
      size: "38 m²",
      features: ["WiFi", "Breakfast"],
    },
    {
      type: "Eco Pod",
      price: "$55",
      description:
        "Innovative eco-friendly capsule with rooftop access and urban views.",
      maxGuests: 2,
      size: "15 m²",
      features: ["WiFi", "Rooftop"],
    },
  ];

  const handleBookRoom = (roomType) => {
    alert(`Booking ${roomType}. Redirecting to booking form...`);
    const element = document.getElementById("booking");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <RoomsSection id="rooms">
      <Container>
        <SectionHeader>
          <Subtitle>Accommodations</Subtitle>
          <Title>
            Our <strong>Rooms</strong> & Suites
          </Title>
          <Description>
            Choose from our selection of thoughtfully designed rooms and suites
          </Description>
        </SectionHeader>

        <RoomsGrid>
          {rooms.map((room, index) => (
            <RoomCard key={index}>
              <RoomImage $roomType={room.type}>
                <PriceTag>
                  {room.price}
                  <span style={{ fontSize: "14px" }}>/night</span>
                </PriceTag>
              </RoomImage>
              <RoomContent>
                <RoomTitle>{room.type}</RoomTitle>
                <RoomDesc>{room.description}</RoomDesc>
                <RoomFeatures>
                  <Feature>
                    <Users size={18} />
                    <span>
                      {room.maxGuests}{" "}
                      {room.maxGuests === 1 ? "Guest" : "Guests"}
                    </span>
                  </Feature>
                  <Feature>
                    <Maximize size={18} />
                    <span>{room.size}</span>
                  </Feature>
                  <Feature>
                    <Wifi size={18} />
                    <span>Free WiFi</span>
                  </Feature>
                  {room.features.includes("Breakfast") && (
                    <Feature>
                      <Coffee size={18} />
                      <span>Breakfast</span>
                    </Feature>
                  )}
                </RoomFeatures>
                <BookButton onClick={() => handleBookRoom(room.type)}>
                  Book Now
                </BookButton>
              </RoomContent>
            </RoomCard>
          ))}
        </RoomsGrid>
      </Container>
    </RoomsSection>
  );
};

export default Rooms;
